import React, { useContext, useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { UserApiContext } from "../../../../UserDetailsContext/UserDetailsContext";
import {
  downloadStandardFormat,
  isString,
  readExcelFile,
  trackUserUploadsMastersFileForBulkUploads,
} from "../../Utility/utility";
import { MyCompanyProfileContext } from "../../../../../../src/MyCompanyProfileContext"; 
import { useMixPanelForTracking } from "../../../../../utils/TrackMixPanel";

// array for Location master headers
const LM_HEADERS = [
  "Sr No.",
  "Location Code",
  "Location*",
  "District",
  "State*",
  "Pincode",
  "Address",
  "Location Type",
  "Customer Name",
  "GST No",
];

/**
 * STEP 1
 * Component for uploading and validating Excel files for new location master data.
 * 
 * @param {number} step - Current step in the upload process.
 * @param {Function} setStep - Function to update the current step.
 * @param {Array} uploadedData - Data extracted from the uploaded file.
 * @param {Function} setUploadedData - Function to update the uploaded data.
 * @param {Object} companyProfile - company profile
 */
const UploadFile = ({ setStep = "", setUploadedData, company_id }) => {
  const [errors, setErrors] = useState([]);
  const [errorTypes, setErrorTypes] = useState({
    wrongFileType: false,
    headersMismatch: false,
    missingMandatoryFields: false,
    shortLocationCode: false,
    invalidLocationType: false,
  });
  
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext); // Fetch company setting data
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);

  const fileInputRef = useRef(null);

  /**
   * Validations for uploaded excel file
   * @param {Array} data - excel data
   * @returns {Array} - returns true if errors are present else false
   */
  const validateData = (data) => {
    let errors = [];
    let newErrorTypes = {};

    if (data.length === 0) {
      errors.push("Please Upload a valid File.");
      return false;
    }

    if (!(data[0]["Location*"] && data[0]["State*"])) {
      if(!isString(data[0]["Location*"] || !isString(data[0]["State*"]))) {
        errors.push(
          "Location Name and state should not be numbers."
        );
        newErrorTypes.missingMandatoryFields = true;
      } else {
        errors.push(
          "Headers do not match the standard format. Please try again."
        );
        newErrorTypes.headersMismatch = true;
      }
    }

    const validLocationTypes = ["destination", "plant", "warehouse"];

    data.forEach((row, index) => {
      if (!row["Location*"] || !row["State*"]) {
        errors.push(
          `Row ${index + 2}: Missing mandatory fields (Location or State)`
        );
        newErrorTypes.missingMandatoryFields = true;
      }
      if (row["Location Code"] && String(row["Location Code"]).length < 3) {
        errors.push(
          `Row ${index + 2}: Location Code should be at least 3 characters long`
        );
        newErrorTypes.shortLocationCode = true;
      }

      if (row["Location Type"]) {
        const locationTypeLower = row["Location Type"].toLowerCase();
        if (
          row["Location Type"] &&
          !validLocationTypes.includes(locationTypeLower)
        ) {
          errors.push(
            `Row ${
              index + 2
            }: Location Type can only be Plant, Warehouse, or Destination`
          );
          newErrorTypes.invalidLocationType = true;
        }
      }
    });

    setErrors(errors);
    setErrorTypes(newErrorTypes);
    return errors.length === 0;
  };

  /**
   * handles file upload and security checks 
   * @param {any} e  -event for file upload
   * @returns {void}
   */
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    resetFileInput();
    if (selectedFile) {
      resetErrors();

      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        setErrorTypes((prev) => ({ ...prev, wrongFileType: true }));
        setErrors(["The file must be in .xlsx or .xls format."]);
        return;
      }

      handleUpload(selectedFile);
    }
  };

  /**
  * Handles the file upload, including validation.
  * @param {Event} e - The file input change event.
  */
  const handleUpload = async (file) => {
    const { data, error } = await readExcelFile(file);

    if (error) {
      toast.error(error);
      return;
    }

    if (validateData(data)) {
      setUploadedData(data);
      setStep(1);
      TrackMixPanel({
        moduleName: "masters",
        screenName: "location master bulk upload",
        definition: "file successfully uploaded and validated",
        data: `file name: ${file.name}, number of records: ${data.length}`,
      });
    }

    trackUserUploadsMastersFileForBulkUploads(
      "location",
      userDetails,
      company_id,
      file.name,
      TrackMixPanel
    );
  };

  /**
   * Reset file input, errors and error types 
   * @returns {void}
   */
  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetErrors = () => {
    setErrors([]);
    setErrorTypes({
      wrongFileType: false,
      headersMismatch: false,
      missingMandatoryFields: false,
      shortLocationCode: false,
    });
  };
  const handleDownloadFormat = () => {
    downloadStandardFormat(LM_HEADERS, "LocationMaster", TrackMixPanel);
    TrackMixPanel({
      moduleName: "masters",
      screenName: "location master",
      definition: "downloaded standard bulk upload format",
      data: `master type: location`,
    });
  };

  return (
    <div className="card-body overflow-auto" style={{ maxHeight: "460px" }}>
      <div className="mb-4 text-center">
        <button
          className="btn btn-primary"
          onClick={handleDownloadFormat}
        >
          Download Standard Format
        </button>
      </div>
      <div className="alert alert-warning" style={{ margin: 0 }}>
        <h5 className="alert-heading under">
          <u>File Requirements</u>:
        </h5>
        <ul>
          <li
            style={{
              color: errorTypes.wrongFileType ? "red" : "inherit",
              fontWeight: errorTypes.wrongFileType ? "bold" : "normal",
            }}
          >
            The file must be in .xlsx or .xls format.
            {errorTypes.wrongFileType && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.headersMismatch ? "red" : "inherit",
              fontWeight: errorTypes.headersMismatch ? "bold" : "normal",
            }}
          >
            Ensure that headers match the standard format.
            {errorTypes.headersMismatch && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.missingMandatoryFields ? "red" : "inherit",
              fontWeight: errorTypes.missingMandatoryFields ? "bold" : "normal",
            }}
          >
            Mandatory fields:{" "}
            {errorTypes.missingMandatoryFields && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
            <ul>
              <li>Location*</li>
              <li>State*</li>
            </ul>
          </li>
          <li
            style={{
              color: errorTypes.shortLocationCode ? "red" : "inherit",
              fontWeight: errorTypes.shortLocationCode ? "bold" : "normal",
            }}
          >
            Location Code should be at least 3 characters long{" "}
            <u>if provided</u>.
            {errorTypes.shortLocationCode && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li
            style={{
              color: errorTypes.invalidLocationType ? "red" : "inherit",
              fontWeight: errorTypes.invalidLocationType ? "bold" : "normal",
            }}
          >
            Location type must be Warehouse, Plant, or Destination,{" "}
            <u>if provided</u>.
            {errorTypes.invalidLocationType && (
              <i
                className="fa fa-exclamation-circle"
                style={{ color: "red" }}
                aria-hidden="true"
              ></i>
            )}
          </li>
          <li>Location Code should be unique to your Location Master.</li>
        </ul>
      </div>
      <div className="text-center">
        <label className="btn btn-success upload-file-button-enquiry-my-account m-4">
          <input
            type="file"
            className="form-control d-none"
            id="upload_file_button"
            onChange={handleFileChange}
            accept=".xlsx,.xls"
            ref={fileInputRef}
          />
          <i className="fas fa-upload mr-2"></i>
          Upload File
        </label>
      </div>
      {errors.length > 0 && (
        <div>
          <h6 className="text-danger">Errors:</h6>
          <ul className="list-group">
            {errors.map((error, index) => (
              <li
                key={index}
                className="list-group-item list-group-item-danger"
              >
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default UploadFile;